/**
 * WebSocket Client for SCO Web Application
 *
 * Browser-compatible WebSocket client with JWT auth and auto-reconnect
 */

const isWsDebugEnabled = () => {
  try {
    return localStorage.getItem("SCO_WS_DEBUG") === "1";
  } catch (_) {
    return false;
  }
};

// Get WebSocket URL from environment
function resolveWsUrl() {
  const envVariable = require('../../EnvironmentVariables.json');
  if(envVariable.serverId=="https://api.svccentral.com/BackEnd"){
    return 'wss://ws.svccentral.com/ws/';
  }
  // If explicit WS_URL is set, use it
  if (envVariable.WS_URL) {
    return envVariable.WS_URL;
  }

  // Otherwise, derive from serverId
  try {
    const server = envVariable.serverId;
    if (server) {
      const url = new URL(server);
      // Use ws:// for development, wss:// for production
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${url.hostname}:8080/`;
    }
  } catch (e) {

    console.error('[WS] Error parsing serverId:', e);
  }

  // Default to localhost
  return 'ws://localhost:8080/';
}

// Fetch WebSocket JWT token
async function fetchWsToken() {
  const envVariable = require('../../EnvironmentVariables.json');
  const api_key = localStorage.getItem('api_key');

  if (!api_key) {
    throw new Error('No API key found');
  }

  const response = await fetch(`${envVariable.serverId}/Auth/ws_token.php`, {
    method: 'GET',
    headers: {
      'X-API-KEY': api_key,
    },
  });

  if (!response.ok) {
    throw new Error(`Token fetch failed: ${response.status}`);
  }

  const data = await response.json();
  if (!data?.token) {
    throw new Error('No token in response');
  }

  return data.token;
}

export class WsClient {
  constructor({
    getToken = fetchWsToken,
    urlResolver = resolveWsUrl,
    onHello,
    onReady,
    onAuthAck,
    onSubscribeAck,
    onNotification,
    onMessageCreated,
    onMessageUpdated,
    onMessageDeleted,
    onResync,
    onError,
    onClose,
    logPrefix = '[WS]',
  } = {}) {
    this.getToken = getToken;
    this.urlResolver = urlResolver;
    this.onHello = onHello;
    this.onReady = onReady;
    this.onAuthAck = onAuthAck;
    this.onSubscribeAck = onSubscribeAck;
    this.onNotification = onNotification;
    this.onMessageCreated = onMessageCreated;
    this.onMessageUpdated = onMessageUpdated;
    this.onMessageDeleted = onMessageDeleted;
    this.onResync = onResync;
    this.onError = onError;
    this.onClose = onClose;
    this.logPrefix = logPrefix;

    this.ws = null;
    this.wsReady = false;
    this.backoff = 1000;
    this.timer = null;
    this.subscriptions = new Set();
    this.closedByApp = false;
  }

  log(...args) {
    if (isWsDebugEnabled()) {
      console.log(this.logPrefix, ...args);
    }
  }

  async connect() {
    this.closedByApp = false;

    try {
      this.token = await this.getToken();
    } catch (e) {
      this.log('❌ Token error:', e.message);
      return this.scheduleReconnect();
    }

    const url = this.urlResolver();
    this.log('🔌 Connecting to', url);

    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      this.log('❌ WebSocket error:', e.message);
      return this.scheduleReconnect();
    }

    this.ws.onopen = () => {
      this.log('✓ Connected, authenticating...');
      try {
        this.ws.send(JSON.stringify({ type: 'auth', token: this.token }));
      } catch (e) {
        this.log('❌ Auth send error:', e.message);
      }
    };

    this.ws.onmessage = (evt) => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        this.log('⚠️ Failed to parse message');
        return;
      }

      this.log('⇐ Received:', msg.type);

      if (msg.type === 'hello') {
        this.onHello?.();
        return;
      }

      if (msg.type === 'ack' && msg.of === 'auth') {
        this.log('✓ Authenticated as user:', msg.user_id);
        this.backoff = 1000;
        this.onAuthAck?.(msg);
        this.resubscribeAll();
        return;
      }

      if (msg.type === 'ack' && msg.of === 'subscribe') {
        this.log('✓ Subscribed to:', msg.channel);
        this.wsReady = true;
        this.onSubscribeAck?.(msg);
        this.onReady?.();
        return;
      }

      if (msg.type === 'notification:new') {
        if (!msg.notification) {
          this.log('⚠️ notification:new without payload');
          return;
        }
        this.log('🔔 Notification:', msg.notification.type);
        this.onNotification?.(msg);
        return;
      }

      if (msg.type === 'message:created') {
        this.log('📨 Message created');
        this.onMessageCreated?.(msg);
        return;
      }

      if (msg.type === 'message:updated') {
        this.log('✏️ Message updated');
        this.onMessageUpdated?.(msg);
        return;
      }

      if (msg.type === 'message:deleted') {
        this.log('🗑️ Message deleted');
        this.onMessageDeleted?.(msg);
        return;
      }

      if (msg.type === 'resync') {
        this.log('🔄 Resync requested');
        this.onResync?.(msg);
        return;
      }

      this.log('⚠️ Unhandled message type:', msg.type);
    };

    this.ws.onerror = (e) => {
      this.log('❌ Error:', e);
      this.onError?.(e);
    };

    this.ws.onclose = () => {
      this.log('Connection closed');
      this.wsReady = false;
      this.onClose?.();

      if (!this.closedByApp) {
        this.scheduleReconnect();
      }
    };
  }

  scheduleReconnect() {
    if (this.closedByApp) return;

    const delay = Math.min(this.backoff, 15000);
    this.log(`⏱️ Reconnecting in ${delay}ms...`);

    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.backoff = Math.min(this.backoff * 2, 15000);
      this.connect();
    }, delay);
  }

  resubscribeAll() {
    if (this.subscriptions.size === 0) {
      this.log('No subscriptions to restore');
      this.wsReady = true;
      this.onReady?.();
      return;
    }

    this.log(`Resubscribing to ${this.subscriptions.size} channels`);
    this.subscriptions.forEach(channel => {
      this.subscribe(channel);
    });
  }

  subscribe(channel) {
    if (!channel) return;

    this.subscriptions.add(channel);

    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'subscribe', channel }));
        this.log('→ Subscribing to:', channel);
      } catch (e) {
        this.log('❌ Subscribe error:', e.message);
      }
    }
  }

  ensureSubscribed(channel) {
    this.subscribe(channel);
  }

  close() {
    this.log('Closing connection...');
    this.closedByApp = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.wsReady = false;
  }
}

export default WsClient;
